import {Component, EventEmitter, Input, Output} from '@angular/core';
import {DateAdapter} from "@angular/material/core";
import {MatDatepicker} from "@angular/material/datepicker";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {IsoWeekDateAdapter} from "../iso-week-date-adapter";

/**
 * The pair of "compare X and Y" pickers used by the comparison chart sections.
 *
 * Split out of dashboards-chart-section so the Monday-first DateAdapter below applies ONLY to
 * these pickers. Providing it on the chart section leaked it to every app-custom-date-filter
 * rendered in a chart section, since those are template children and inherit its injector.
 *
 * The adapter only changes day-grid layout, so it is a no-op for the 'month' and 'year' picker
 * types, which never open on a day grid.
 */
@Component({
  selector: 'app-comparison-date-picker',
  templateUrl: './comparison-date-picker.component.html',
  styleUrls: ['./comparison-date-picker.component.scss'],
  providers: [{provide: DateAdapter, useClass: IsoWeekDateAdapter}]
})
export class ComparisonDatePickerComponent {
  @Input() pickerType?: "month" | "year" | "week";
  @Input() date1: Date = new Date();
  @Input() date2: Date = new Date();

  @Output() date1Change = new EventEmitter<Date>();
  @Output() date2Change = new EventEmitter<Date>();
  @Output() datePickerClosed = new EventEmitter<void>();

  constructor(public dateUtils: DateUtilsService) {
  }

  /**
   * Scopes the week-specific calendar styling to the calendars this component opens. The calendar
   * renders in a CDK overlay outside this component's DOM, so component styles cannot reach it —
   * the panel class is what keeps those rules off every other datepicker in the app.
   */
  get calendarPanelClass(): string {
    return this.pickerType == "week" ? 'iso-week-calendar' : '';
  }

  closeDatePicker(event: any, picker: MatDatepicker<any>, pickerIndex: number) {
    if (pickerIndex === 1) {
      this.date1 = event;
      this.date1Change.emit(this.date1);
    }
    if (pickerIndex === 2) {
      this.date2 = event;
      this.date2Change.emit(this.date2);
    }
    picker.close();
    this.datePickerClosed.emit();
  }

  get getPickerStartView(): "multi-year" | "year" | "month" {
    if (this.pickerType == "year") return "multi-year";
    if (this.pickerType == "month") return "year"
    return "month";
  }

  selectYear(event: any, picker: MatDatepicker<any>, pickerIndex: number) {
    if (this.pickerType == "year") this.closeDatePicker(event, picker, pickerIndex);
  }

  selectMonth(event: any, picker: MatDatepicker<any>, pickerIndex: number) {
    if (this.pickerType == "month") this.closeDatePicker(event, picker, pickerIndex);
  }

  /**
   * Material has no week view, so the day view stands in for one: whichever day is clicked is
   * snapped back to the Monday of its ISO week before being emitted.
   */
  selectWeek(event: any, picker: MatDatepicker<any>, pickerIndex: number) {
    if (this.pickerType != "week" || !event) return;
    this.closeDatePicker(this.dateUtils.startOfIsoWeek(event), picker, pickerIndex);
  }

  /** Highlights every day of the selected week, so the day view reads as a week picker. */
  weekHighlightFor(pickerIndex: number) {
    const selected = pickerIndex === 1 ? this.date1 : this.date2;
    return (date: Date): string =>
      this.pickerType == "week" && this.dateUtils.isSameIsoWeek(date, selected) ? 'selected-iso-week' : '';
  }

  pickerDisplayValue(date: Date): string {
    if (this.pickerType == "year") return this.dateUtils.displayYear(date!)!;
    if (this.pickerType == "month") return this.dateUtils.displayMonthAndYear(date!)!;
    if (this.pickerType == "week") return this.dateUtils.displayIsoWeek(date!)!;
    return "";
  }

  pickerTooltip(date: Date): string {
    if (this.pickerType == "week") return this.dateUtils.displayIsoWeekRange(date!)!;
    return "";
  }
}
